import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import { validateOfferToken, acceptOffer, rejectOffer, respondToReformulation, downloadOfferPdf, logoutUser } from "../services/api";
import { FONTS, COLORS, BREAKPOINTS } from "../utils/styleConstants";
import SidebarLogo from "../assets/sidebar_logo.svg";
import OfferDisplay from "../components/oferta/OfferDisplay";
import OfferActionButtons from "../components/oferta/OfferActionButtons";
import AcceptModal from "../components/oferta/AcceptModal";
import RejectModal from "../components/oferta/RejectModal";
import NotificationMessage from "../components/NotificationMessage";
import LoadingIndicator from "../components/LoadingIndicator";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";

const DARK_BLUE = "#20376e";
const ACCENT_ORANGE = "#ff7057";

// Main container styles
const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: var(--secondary-color);
  display: flex;
  box-shadow: var(--box-shadow);
  overflow: hidden;
  height: 100vh;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 3rem;
  border-radius: 1rem 0 0 1rem;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: white;
  }
`;

const SidebarLogoStyled = styled.img`
  width: 40px;
  height: auto;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    width: 30px;
    margin-bottom: 0;
    margin-left: 1rem;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 2rem;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
  align-items: start;
  flex-shrink: 0;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    margin-bottom: 2rem;
  }
`;

const HeaderTitle = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Description = styled.p`
  color: var(--accent-color);
  font-size: 1.1rem;
  font-family: "AtHauss", "Avenir Next", ${FONTS.PRIMARY};
  margin-top: 0.5rem;
  margin-bottom: 0;
  line-height: 1.5;
  width: 100%;
  max-width: 60%;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    max-width: 100%;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const ConfidentialTag = styled.span`
  display: inline-block;
  background-color: #ffeded;
  color: #d32f2f;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.85rem;
  margin-left: 1rem;
  font-weight: 600;
`;

const OfferResponsePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Store the action and token if auth is required
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    const fetchOfferData = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");
        
        if (!token) {
          setError({
            title: "Token no encontrado",
            message: "No se ha proporcionado un token válido para acceder a la oferta."
          });
          return;
        }
        
        const response = await validateOfferToken(token);
        
        if (response && response.valid) {
          // Guardar el token con los datos de la oferta
          const offerData = {
            ...response.offer,
            token
          };
          
          setOffer(offerData);
          
          // Si el API indica redirección al login
          if (response.requiresLogin) {
            // Guardar el token en sessionStorage para recuperarlo después del login
            sessionStorage.setItem("pendingOfferToken", token);
            sessionStorage.setItem("pendingRedirectUrl", `/offers/respond?token=${token}`);
            
            // Si el usuario está autenticado, cerrar la sesión primero
            if (isAuthenticated) {
              try {
                // Eliminamos solo el token local sin redirigir (logout silencioso)
                localStorage.removeItem("token");
                // No llamamos a logoutUser() porque eso nos redireccionaría a login directamente
                // y queremos controlar el flujo aquí
              } catch (err) {
                console.error("Error al cerrar la sesión:", err);
              }
            }
            
            // Redirigir al login
            navigate("/login", { state: { from: location } });
            return;
          }
          
          // Si el API indica que el usuario actual no está autorizado
          if (response.invalidUser) {
            setError({
              title: "Usuario no autorizado",
              message: "El usuario actual no está autorizado para acceder a esta oferta. La oferta solo puede ser vista por el destinatario o su abogado."
            });
            return;
          }
          
          // Check if there's a pending action stored in session storage
          const storedAction = sessionStorage.getItem("pendingOfferAction");
          const storedToken = sessionStorage.getItem("pendingOfferToken");
          
          if (storedAction && storedToken === token && isAuthenticated) {
            // Execute the pending action
            if (storedAction === "accept") {
              setShowAcceptModal(true);
            } else if (storedAction === "reject") {
              setShowRejectModal(true);
            } else if (storedAction === "counteroffer") {
              // Navigate to counteroffer page
              navigate(`/offers/counteroffer?token=${token}`);
            }
            
            // Clear the stored action
            sessionStorage.removeItem("pendingOfferAction");
            sessionStorage.removeItem("pendingOfferToken");
            sessionStorage.removeItem("pendingRedirectUrl");
          }
        } else {
          // Manejar casos especiales para ciertos errores
          if (response && response.error === "invalid_offer_status") {
            // Mostrar un mensaje informativo en vez de error para este caso específico
            setSuccessMessage({
              title: "Oferta ya procesada",
              message: "Esta oferta ya ha sido respondida y no requiere ninguna acción adicional. Si crees que esto es un error, contacta con soporte."
            });
          } else {
            // Para otros casos, mostrar el error estándar
            setError({
              title: "Oferta no disponible",
              message: response?.message || "No se puede acceder a esta oferta. El token puede ser inválido o la oferta ha expirado."
            });
          }
        }
      } catch (err) {
        console.error("Error fetching offer:", err);
        
        // Verificar si es el error específico de estado de oferta inválido
        if (err.message && (
            err.message.includes("invalid_offer_status") || 
            err.message.includes("La oferta no está en estado para ser respondida")
          )) {
          // Mostrar mensaje informativo en vez de error
          setSuccessMessage({
            title: "Oferta ya procesada",
            message: "Esta oferta ya ha sido respondida y no requiere ninguna acción adicional. Si crees que esto es un error, contacta con soporte."
          });
        } else {
          // Para otros errores, mostrar mensaje genérico de error
          setError({
            title: "Error al cargar la oferta",
            message: "Ha ocurrido un error al intentar cargar los detalles de la oferta. Por favor, inténtelo de nuevo más tarde."
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchOfferData();
  }, [location.search, isAuthenticated]);

  const checkAuthAndProceed = (action) => {
    // For this implementation, we'll assume all actions require authentication
    // In a real implementation, this could be configurable based on backend settings
    if (!isAuthenticated) {
      // Store the pending action and token
      sessionStorage.setItem("pendingOfferAction", action);
      sessionStorage.setItem("pendingOfferToken", offer.token);
      sessionStorage.setItem("pendingRedirectUrl", `/offers/respond?token=${offer.token}`);
      
      // Redirect to login
      navigate("/login", { state: { from: location } });
      return false;
    }
    
    return true;
  };

  const handleAccept = async (acceptanceData) => {
    if (!checkAuthAndProceed("accept")) return;
    
    setIsProcessing(true);
    try {
      let response;
      
      // Check if this is a reformulated offer
      if (offer.status === 'reformulated') {
        response = await respondToReformulation(offer.id, 'accept', acceptanceData);
        setSuccessMessage({
          title: "Reformulación aceptada",
          message: "Has aceptado la reformulación correctamente. Se notificará al remitente de tu decisión."
        });
      } else {
        response = await acceptOffer(offer.id, offer.token, acceptanceData);
        setSuccessMessage({
          title: "Oferta aceptada",
          message: "Has aceptado la oferta correctamente. Se notificará al remitente de tu decisión."
        });
      }
      
      setShowAcceptModal(false);
      
      // After 3 seconds, redirect to home
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      setError({
        title: "Error al aceptar",
        message: "Ha ocurrido un error al intentar aceptar la oferta. Por favor, inténtelo de nuevo más tarde."
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (reason) => {
    if (!checkAuthAndProceed("reject")) return;
    
    setIsProcessing(true);
    try {
      let response;
      
      // Check if this is a reformulated offer
      if (offer.status === 'reformulated') {
        response = await respondToReformulation(offer.id, 'reject', reason);
        setSuccessMessage({
          title: "Reformulación rechazada",
          message: "Has rechazado la reformulación correctamente. Se notificará al remitente de tu decisión."
        });
      } else {
        response = await rejectOffer(offer.id, offer.token, reason);
        setSuccessMessage({
          title: "Oferta rechazada",
          message: "Has rechazado la oferta correctamente. Se notificará al remitente de tu decisión."
        });
      }
      
      setShowRejectModal(false);
      
      // After 3 seconds, redirect to home
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      setError({
        title: "Error al rechazar",
        message: "Ha ocurrido un error al intentar rechazar la oferta. Por favor, inténtelo de nuevo más tarde."
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCounteroffer = () => {
    if (!checkAuthAndProceed("counteroffer")) return;
    
    // Navigate to the counteroffer page with the token
    navigate(`/offers/counteroffer?token=${offer.token}`);
  };

  const handleDownloadPdf = async () => {
    try {
      await downloadOfferPdf(offer.id, offer.token);
    } catch (error) {
      setError({
        title: "Error al descargar PDF",
        message: "Ha ocurrido un error al intentar descargar el PDF de la oferta. Por favor, inténtelo de nuevo más tarde."
      });
    }
  };
  
  const handleCloseError = () => {
    setError(null);
  };

  // Render different content based on state
  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingIndicator message="Cargando detalles de la oferta..." />
      );
    }

    if (error) {
      return (
        <NotificationMessage 
          title={error.title} 
          message={error.message} 
          isError={true} 
        />
      );
    }

    if (successMessage) {
      return (
        <NotificationMessage 
          title={successMessage.title} 
          message={successMessage.message} 
          isError={false} 
        />
      );
    }

    if (!offer) {
      return (
        <NotificationMessage 
          title="Oferta no disponible" 
          message="No se ha podido cargar la información de la oferta. Por favor, verifique el enlace o contacte con soporte." 
          isError={true} 
        />
      );
    }

    return (
      <>
        <HeaderContainer>
          <HeaderContent>
            <HeaderTitle>
              {offer.status === 'reformulated' 
                ? "Reformulación de Oferta Vinculante"
                : "Oferta Vinculante"
              }
            </HeaderTitle>
            <Description>
              {offer.status === 'reformulated' 
                ? "El oferente ha reformulado su oferta original. Esta es la nueva propuesta que puedes aceptar o rechazar. Ten en cuenta que esta reformulación es legalmente vinculante si la aceptas."
                : "Este documento es una Oferta Vinculante. Recuerda que una Oferta Vinculante legalmente obliga a la parte oferente a cumplir con los términos estipulados. Al final, de la Oferta Vinculante podrás escoger una de las tres opciones: aceptar, rechazar o hacer una contraoferta."
              }
            </Description>
          </HeaderContent>
        </HeaderContainer>
        
        <OfferDisplay offer={offer} />
        
        <OfferActionButtons 
          onAccept={() => setShowAcceptModal(true)} 
          onReject={() => setShowRejectModal(true)} 
          onCounteroffer={handleCounteroffer} 
          onDownloadPdf={handleDownloadPdf} 
          disabled={isProcessing}
          showCounteroffer={offer.status !== 'reformulated'}
        />
        
        <Footer />
      </>
    );
  };

  return (
    <PageContainer>
      <Sidebar>
        <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
      </Sidebar>
      
      <ContentArea>
        <ContentContainer>
          {renderContent()}
        </ContentContainer>
        
        <AcceptModal 
          isOpen={showAcceptModal} 
          onClose={() => setShowAcceptModal(false)} 
          onConfirm={(comments) => handleAccept(comments)} 
          isProcessing={isProcessing}
          offer={offer}
        />
        
        <RejectModal 
          isOpen={showRejectModal} 
          onClose={() => setShowRejectModal(false)} 
          onConfirm={(reason) => handleReject(reason)} 
          isProcessing={isProcessing} 
        />
      </ContentArea>
    </PageContainer>
  );
};

export default OfferResponsePage; 